import { createStore } from 'zustand/vanilla';
import type { ShapeSelection, ShapeType, ShapePropsByType } from '../types/commontypes';
import type { SymbolType } from '../utils/chartHandlers';

// ─── Types ─────────────────────────────────────────────────────────

/** engview가 push하는 포지션 정보의 최소 인터페이스. PositionStatus 전체를 넘겨도 구조적 타이핑으로 호환됨. */
export interface MarketPositionRecord {
  positions?: Record<string, { contracts?: number } | null | undefined>;
}

interface GlobalShapeState {
  /** 현재 툴바에서 선택된 도형 타입 */
  shapeTypeToDraw: ShapeType | undefined;
  /** 도형 목록 사이드바 표시 여부 */
  isShapeListViewOpen: boolean;
  /** 도형 선택 하이라이팅 활성화 */
  enableShapeSelection: boolean;
  /** 현재 활성 캔버스 (symbol + timeframe) */
  selectedCanvasId: { symbol: string; timeframe: string } | undefined;
  /** 현재 선택된 심볼 */
  selectedSymbol: SymbolType;
  /** 현재 선택된 실제 도형 */
  selectedShape: ShapeSelection | null;
  /**
   * 도형 선택 플래그 — onSelect(mousedown)에서 true로 설정, handleClick(setTimeout)에서 읽고 리셋.
   * zustand state가 아닌 동기적 플래그이므로 mutable 필드로 관리한다.
   */
  shapeJustSelected: boolean;
  /** 도형별 마지막 사용 속성 (localStorage 영속화) */
  lastShapeProperties: Partial<Record<ShapeType, ShapePropsByType[ShapeType]>>;
  /** 거래소 헷지모드 ON/OFF — engview가 push (타임프레임 무관 전역 설정) */
  hedgeMode: boolean;
  /** 방향별 마지막 TP/SL 비율 (localStorage 영속화) */
  lastTPSLByDirection: { long: number; short: number; hedge: number };
  /** engview가 push하는 현재 활성 타임프레임 목록 (런타임 상태, 영속화하지 않음) */
  activeTimeframes: string[];
  /** engview가 push하는 포지션 데이터 (런타임 상태, 영속화하지 않음) */
  marketPositions: Record<string, MarketPositionRecord | null | undefined>;
}

interface GlobalShapeActions {
  setShapeTypeToDraw: (shape: ShapeType | undefined) => void;
  toggleShapeList: () => void;
  setIsShapeListVisible: (visible: boolean) => void;
  setEnableShapeSelection: (enabled: boolean) => void;
  setSelectedCanvasId: (
    canvasId:
      | { symbol: string; timeframe: string }
      | undefined
      | ((
          prev: { symbol: string; timeframe: string } | undefined,
        ) => { symbol: string; timeframe: string } | undefined),
  ) => void;
  setSelectedSymbol: (symbol: SymbolType | ((prev: SymbolType) => SymbolType)) => void;
  setSelectedShape: (shape: ShapeSelection | null | ((prev: ShapeSelection | null) => ShapeSelection | null)) => void;
  setShapeJustSelected: (flag: boolean) => void;
  setLastShapeProperties: (props: Partial<Record<ShapeType, ShapePropsByType[ShapeType]>>) => void;
  updateLastShapeProperty: (type: ShapeType, props: ShapePropsByType[ShapeType]) => void;
  setHedgeMode: (v: boolean) => void;
  setLastTPSLByDirection: (dir: { long: number; short: number; hedge: number }) => void;
  updateLastTPSLByDirection: (key: 'long' | 'short' | 'hedge', value: number) => void;
  setActiveTimeframes: (tfs: string[]) => void;
  setMarketPositions: (positions: Record<string, MarketPositionRecord | null | undefined>) => void;
}

export type GlobalShapeStore = GlobalShapeState & GlobalShapeActions;

// ─── localStorage 연동 ────────────────────────────────────────────

const LAST_SHAPE_PROPS_KEY = 'lastShapeProperties';
const LAST_TPSL_BY_DIR_KEY = 'lastTPSLByDirection';

function loadLastShapeProperties(): Partial<Record<ShapeType, ShapePropsByType[ShapeType]>> {
  try {
    const stored = localStorage.getItem(LAST_SHAPE_PROPS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

const DEFAULT_TPSL_BY_DIR = { long: 2, short: 2, hedge: 2 };

function loadLastTPSLByDirection(): { long: number; short: number; hedge: number } {
  try {
    const stored = localStorage.getItem(LAST_TPSL_BY_DIR_KEY);
    return stored ? { ...DEFAULT_TPSL_BY_DIR, ...JSON.parse(stored) } : { ...DEFAULT_TPSL_BY_DIR };
  } catch {
    return { ...DEFAULT_TPSL_BY_DIR };
  }
}

let saveTpslTimer: ReturnType<typeof setTimeout> | undefined;
function saveLastTPSLByDirection(data: { long: number; short: number; hedge: number }): void {
  clearTimeout(saveTpslTimer);
  saveTpslTimer = setTimeout(() => {
    try {
      localStorage.setItem(LAST_TPSL_BY_DIR_KEY, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }, 500);
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;
export function saveLastShapeProperties(data: Partial<Record<ShapeType, ShapePropsByType[ShapeType]>>): void {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      localStorage.setItem(LAST_SHAPE_PROPS_KEY, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }, 500);
}

// ─── Store ─────────────────────────────────────────────────────────

export const globalShapeStore = createStore<GlobalShapeStore>((set, get) => ({
  // ── State ──
  shapeTypeToDraw: undefined,
  isShapeListViewOpen: true,
  enableShapeSelection: false,
  selectedCanvasId: undefined,
  selectedSymbol: 'BTC',
  selectedShape: null,
  shapeJustSelected: false,
  lastShapeProperties: loadLastShapeProperties(),
  hedgeMode: false,
  lastTPSLByDirection: loadLastTPSLByDirection(),
  activeTimeframes: [],
  marketPositions: {},

  // ── Actions ──
  setShapeTypeToDraw: shape => set({ shapeTypeToDraw: shape }),

  toggleShapeList: () => set(s => ({ isShapeListViewOpen: !s.isShapeListViewOpen })),

  setIsShapeListVisible: visible => set({ isShapeListViewOpen: visible }),

  setEnableShapeSelection: enabled => set({ enableShapeSelection: enabled }),

  setSelectedCanvasId: canvasIdOrUpdater =>
    set(s => ({
      selectedCanvasId:
        typeof canvasIdOrUpdater === 'function' ? canvasIdOrUpdater(s.selectedCanvasId) : canvasIdOrUpdater,
    })),

  setSelectedSymbol: symbolOrUpdater =>
    set(s => ({
      selectedSymbol: typeof symbolOrUpdater === 'function' ? symbolOrUpdater(s.selectedSymbol) : symbolOrUpdater,
    })),

  setSelectedShape: shapeOrUpdater =>
    set(s => ({
      selectedShape: typeof shapeOrUpdater === 'function' ? shapeOrUpdater(s.selectedShape) : shapeOrUpdater,
    })),

  setShapeJustSelected: flag => set({ shapeJustSelected: flag }),

  setLastShapeProperties: props => {
    set({ lastShapeProperties: props });
    saveLastShapeProperties(props);
  },

  updateLastShapeProperty: (type, props) => {
    const next = { ...get().lastShapeProperties, [type]: props };
    set({ lastShapeProperties: next });
    saveLastShapeProperties(next);
  },

  setHedgeMode: v => set({ hedgeMode: v }),

  setLastTPSLByDirection: dir => {
    set({ lastTPSLByDirection: dir });
    saveLastTPSLByDirection(dir);
  },

  updateLastTPSLByDirection: (key, value) => {
    const prev = get().lastTPSLByDirection;
    const next = { ...prev, [key]: value };
    // long === short가 되는 순간 hedge도 동기화
    if ((key === 'long' && value === next.short) || (key === 'short' && value === next.long)) {
      next.hedge = value;
    }
    set({ lastTPSLByDirection: next });
    saveLastTPSLByDirection(next);
  },

  setActiveTimeframes: tfs => set({ activeTimeframes: tfs }),

  setMarketPositions: positions => set({ marketPositions: positions }),
}));
